// ---------------------------------------------------------------------------
// HDDN LAB marketing site — shared primitives.
//
// Thin wrappers over the classes in theme.js. They exist so a page file reads
// as content rather than markup, and so the number motif, the fork and the rate
// table behave identically everywhere.
// ---------------------------------------------------------------------------

import React from 'react';

export function Shell({ narrow, className = '', children }) {
  return <div className={`hddn-shell${narrow ? ' hddn-shell--narrow' : ''} ${className}`}>{children}</div>;
}

export function Bay({ band, tight, id, narrow, className = '', children }) {
  const bandClass = band ? ` hddn-band--${band}` : '';
  return (
    <section id={id} className={`hddn-bay${tight ? ' hddn-bay--tight' : ''}${bandClass} ${className}`}>
      <Shell narrow={narrow}>{children}</Shell>
    </section>
  );
}

export function Eyebrow({ plain, children }) {
  return <p className={`hddn-eyebrow${plain ? ' hddn-eyebrow--plain' : ''}`}>{children}</p>;
}

// Section head: eyebrow, heading, optional lede. Used on nearly every block, so
// the vertical rhythm stays the same across pages.
export function Head({ eyebrow, title, lede, level = 2, children }) {
  const H = `h${level}`;
  return (
    <header style={{ maxWidth: '54ch', marginBottom: 'var(--gap)' }}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <H className={level === 1 ? 'hddn-h1' : 'hddn-h2'} style={{ marginTop: eyebrow ? 14 : 0 }}>{title}</H>
      {lede ? <p className="hddn-lede" style={{ marginTop: 16 }}>{lede}</p> : null}
      {children}
    </header>
  );
}

export function Btn({ href, onClick, variant = 'fill', wide, children, ...rest }) {
  const cls = `hddn-btn hddn-btn--${variant}${wide ? ' hddn-btn--wide' : ''}`;
  if (href) return <a className={cls} href={href} onClick={onClick} {...rest}>{children}</a>;
  return <button type="button" className={cls} onClick={onClick} {...rest}>{children}</button>;
}

// The number motif. `n` may contain an <em> via the `accent` flag to tint the
// unit — large tabular mono figures are the brand's signature, so they get a
// component rather than ad-hoc markup.
export function Figures({ items, cols = 4 }) {
  if (!items || !items.length) return null;
  return (
    <div className={`hddn-figs hddn-figs--${cols === 3 ? 3 : 4}`}>
      {items.map((f, i) => (
        <div className="hddn-fig" key={f.k || i}>
          <span className="hddn-fig__n">{f.n}</span>
          <span className="hddn-fig__k">{f.k}</span>
          {f.note ? <span className="hddn-fig__note">{f.note}</span> : null}
        </div>
      ))}
    </div>
  );
}

export function Ticks({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul className="hddn-ticks">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

export function Card({ children, className = '', ...rest }) {
  return <div className={`hddn-card ${className}`} {...rest}>{children}</div>;
}

export function Rates({ caption, rows, foot }) {
  return (
    <div>
      <table className="hddn-rates">
        {caption ? <caption className="hddn-vis-hidden">{caption}</caption> : null}
        <thead>
          <tr><th scope="col">Block</th><th scope="col">Rate</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || r.label}>
              <th scope="row" style={{ fontFamily: 'var(--sans)', textTransform: 'none', letterSpacing: 0, fontSize: 'var(--step-0)', fontWeight: 700, color: 'var(--ink)' }}>
                {r.label}
                {r.sub ? <span className="hddn-sub">{r.sub}</span> : null}
              </th>
              <td>
                <span className="hddn-price">${r.price}{r.unit || ''}</span>
                {r.foot ? <span className="hddn-sub">{r.foot}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {foot ? <p className="hddn-note" style={{ marginTop: 14 }}>{foot}</p> : null}
    </div>
  );
}

// Lazily-loaded image in a fixed-ratio frame. Explicit dimensions and
// `loading="lazy"` on every one of these is most of the site's image budget.
export function Frame({ src, alt, ratio = 'wide', eager, children }) {
  return (
    <figure className={`hddn-frame hddn-frame--${ratio}`}>
      {src ? (
        <img
          src={src}
          alt={alt || ''}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          width={ratio === 'tall' ? 800 : 1200}
          height={ratio === 'tall' ? 1000 : ratio === 'square' ? 1200 : 675}
        />
      ) : null}
      {children}
    </figure>
  );
}

// Horizontal, snap-scrolling photo strip. Renders nothing when there are no
// photos rather than showing an empty well.
export function PhotoStrip({ photos, alt, ratio = 'wide', limit = 10 }) {
  const list = (photos || []).slice(0, limit);
  if (!list.length) return null;
  return (
    <div className="hddn-strip" role="group" aria-label={alt}>
      {list.map((p, i) => (
        <Frame key={p.id || i} src={p.url} alt={p.caption || `${alt} — ${i + 1}`} ratio={ratio} />
      ))}
    </div>
  );
}

export function Defs({ items }) {
  if (!items || !items.length) return null;
  return (
    <dl className="hddn-defs">
      {items.map((d, i) => (
        <div className="hddn-def" key={d.t || i}>
          <dt>{d.t}</dt>
          <dd>{d.d}</dd>
        </div>
      ))}
    </dl>
  );
}

// The cross-link that keeps the two halves aware of each other. Every page ends
// with one pointing at the door the visitor did not come through.
export function OtherDoor({ k, title, body, href, cta }) {
  return (
    <Bay band="ground" tight>
      <div className="hddn-grid hddn-grid--sidebar">
        <div>
          <Eyebrow>{k}</Eyebrow>
          <h2 className="hddn-h3" style={{ marginTop: 14 }}>{title}</h2>
          <p className="hddn-lede" style={{ marginTop: 14 }}>{body}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
          <Btn href={href} variant="fill" wide>{cta}</Btn>
        </div>
      </div>
    </Bay>
  );
}
